/**
 * Extend Word List Dialog Component
 *
 * Dialog for extending an existing word list with more AI-generated words.
 * The AI generates new words that complement the existing list.
 */

import { useState } from 'react';
import toast from 'react-hot-toast';
import { useExtendWordList } from '../services/wordListsApi';

interface ExtendWordListDialogProps {
  listId: string;
  listName: string;
  currentWordCount: number;
  onClose: () => void;
  onSuccess: () => void;
}

export function ExtendWordListDialog({
  listId,
  listName,
  currentWordCount,
  onClose,
  onSuccess,
}: ExtendWordListDialogProps) {
  const [prompt, setPrompt] = useState('');

  const extendWordList = useExtendWordList();

  const handleSubmit = async () => {
    if (!prompt.trim()) {
      toast.error('Please enter a prompt describing the words to add');
      return;
    }

    if (prompt.trim().length < 5) {
      toast.error('Prompt must be at least 5 characters long');
      return;
    }

    try {
      const updatedList = await extendWordList.mutateAsync({
        id: listId,
        data: { prompt: prompt.trim() },
      });

      const newWordsAdded = updatedList.wordCount - currentWordCount;
      toast.success(
        `Added ${newWordsAdded} new word${newWordsAdded !== 1 ? 's' : ''} to "${listName}"`
      );
      onSuccess();
      onClose();
    } catch (error) {
      toast.error(`Extension failed: ${(error as Error).message}`);
    }
  };

  const isExtending = extendWordList.isPending;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-semibold">Extend Word List with AI</h2>
              <div className="relative group">
                <svg
                  className="w-5 h-5 text-gray-400 hover:text-gray-600 cursor-help"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <div className="absolute top-full left-0 mt-2 hidden group-hover:block z-[100]">
                  <div
                    className="absolute bottom-full left-4 border-8 border-transparent border-b-white"
                    style={{ filter: 'drop-shadow(0 -1px 1px rgba(0,0,0,0.1))' }}
                  />
                  <div className="bg-white text-gray-800 text-sm rounded-lg p-4 w-72 shadow-xl border border-gray-200">
                    <div className="font-semibold text-gray-900 mb-2">What will be generated:</div>
                    <ul className="space-y-1.5 text-gray-600">
                      <li>• New words with example sentences</li>
                      <li>• Translations to all languages</li>
                      <li>• Pronunciations for each word</li>
                      <li>• Images for new words</li>
                    </ul>
                    <div className="mt-2 pt-2 border-t border-gray-200 text-gray-500 text-xs">
                      Existing words in the list will be preserved. Duplicate words are skipped.
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
              disabled={isExtending}
            >
              x
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {isExtending ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4" />
              <div className="text-lg font-medium text-gray-900">Extending word list...</div>
              <div className="text-sm text-gray-500 mt-2">
                This may take a minute. The AI is generating new words, example sentences,
                translations, pronunciations, and images.
              </div>
            </div>
          ) : (
            <>
              {/* List info */}
              <div className="mb-6 bg-gray-50 rounded-lg p-4">
                <div className="text-sm text-gray-600">
                  Extending: <span className="font-medium text-gray-900">{listName}</span>
                </div>
                <div className="text-sm text-gray-500 mt-1">
                  Currently has {currentWordCount} word{currentWordCount !== 1 ? 's' : ''}
                </div>
              </div>

              {/* Prompt Input */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Describe the words you want to add *
                </label>
                <textarea
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  rows={4}
                  placeholder="Examples:
- add 10 more advanced cooking verbs
- include words about kitchen appliances
- extend with 5 formal greetings
- add synonyms for existing words"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Be specific about what kinds of words you want to add. The AI will avoid adding
                  duplicates and maintain thematic consistency with the existing list.
                </p>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-between">
          <button
            onClick={onClose}
            disabled={isExtending}
            className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>

          <button
            onClick={handleSubmit}
            disabled={isExtending || !prompt.trim()}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isExtending ? 'Extending...' : 'Extend List'}
          </button>
        </div>
      </div>
    </div>
  );
}
